import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { setAuthToken } from "../../lib/api-client";
import { loginRequest, verifyMfaLogin } from "./api";
import { AuthContext, type AuthContextValue } from "./context";
import { MfaRequiredError } from "./errors";
import { getRoleDashboardPath, mapBackendRoleToAppRole } from "./role";
import {
  clearStoredSession,
  readStoredSession,
  writeStoredSession,
} from "./storage";
import { getTokenExpiryMs } from "./token";
import type { AuthSession, LoginPayload, MfaTokenResponse } from "./types";

function getFallbackDashboardPath(session: AuthSession | null): string {
  if (!session) return "/login";
  return getRoleDashboardPath(session.appRole);
}

function buildSession(response: MfaTokenResponse, username: string): AuthSession {
  const mappedRole = mapBackendRoleToAppRole(response.role);
  if (!mappedRole) {
    throw new Error(`Unsupported role '${response.role}' for dashboard access`);
  }
  return {
    token: response.access_token,
    username,
    backendRole: response.role,
    appRole: mappedRole,
    companyCode: response.company_code,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Set the api-client's bearer token synchronously here, in the useState
  // initializer, rather than in a useEffect below. React fires child effects
  // before parent effects, so on a hard refresh a descendant's mount-time
  // fetch (e.g. useDashboardData) would otherwise run before this provider's
  // effect had a chance to restore the token -- the request went out with no
  // Authorization header, got a 401, and that 401 triggered an automatic
  // logout. It looked like "refreshing logs me out" but the session was
  // never actually invalid.
  const [session, setSession] = useState<AuthSession | null>(() => {
    const stored = readStoredSession();
    setAuthToken(stored?.token ?? null);
    return stored;
  });

  useEffect(() => {
    setAuthToken(session?.token ?? null);
  }, [session]);

  const login = useCallback(async (payload: LoginPayload) => {
    const response = await loginRequest(payload);

    if (response.mfa_required) {
      throw new MfaRequiredError(response.mfa_challenge_token!);
    }

    const nextSession = buildSession(
      {
        access_token: response.access_token!,
        token_type: response.token_type!,
        role: response.role!,
        company_code: response.company_code!,
      },
      payload.username,
    );

    setAuthToken(nextSession.token);
    writeStoredSession(nextSession);
    setSession(nextSession);
    return nextSession;
  }, []);

  const verifyMfa = useCallback(async (challengeToken: string, code: string, username: string) => {
    const response = await verifyMfaLogin(challengeToken, code);
    const nextSession = buildSession(response, username);

    setAuthToken(nextSession.token);
    writeStoredSession(nextSession);
    setSession(nextSession);
    return nextSession;
  }, []);

  const logout = useCallback(() => {
    setAuthToken(null);
    clearStoredSession();
    setSession(null);
  }, []);

  // Proactively end the session the moment the JWT expires, so expiry lands
  // the user cleanly back on the login screen instead of surfacing as a
  // failed request mid-action. The server remains the real authority -- this
  // just avoids the abrupt "next click 401s" experience.
  useEffect(() => {
    if (!session) return;
    const expiryMs = getTokenExpiryMs(session.token);
    if (expiryMs == null) return;
    // Clamp to >= 0 so an already-expired token logs out on the next tick
    // rather than calling logout() synchronously inside the effect.
    const delay = Math.max(0, expiryMs - Date.now());
    const timer = setTimeout(logout, delay);
    return () => clearTimeout(timer);
  }, [session, logout]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      isAuthenticated: Boolean(session),
      role: session?.appRole ?? null,
      dashboardPath: getFallbackDashboardPath(session),
      login,
      verifyMfa,
      logout,
    }),
    [login, verifyMfa, logout, session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
